import {isFeatureEnabled} from 'feature-flags';
import {Alert, Button, Card, DataStatus, Flag, openDialog, type Action} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  useLazyGetConfigurationDetailQuery,
  useListBusinessCaseConfigurationsQuery,
} from '@dms/api/new-vehicle';
import {type BusinessCaseState, useGetOfferAlternativesQuery} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError, usePermissions} from '@dms/shared';

import {composePath, useNavigate, useOnMount} from 'shared/web';

import {holdsBusinessCaseSlot} from '../../utils/holdsBusinessCaseSlot';
import {ConfigurationsTable} from '../ConfigurationsTable/ConfigurationsTable';
import {getOrderConfigurationDialogId} from '../OrderConfigurationDialog/getOrderConfigurationDialogId';
import {OrderConfigurationDialog} from '../OrderConfigurationDialog/OrderConfigurationDialog';
import {SelectConfigurationDialog} from '../SelectConfigurationDialog/SelectConfigurationDialog';
import {NewVehicleSlotChoice} from './NewVehicleSlotChoice';

export type BusinessCaseConfigurationsCardProps = {
  businessCaseId: string;
  businessCaseState?: BusinessCaseState | null;
  /**
   * A Business Case sells one vehicle. Once that slot is filled the backend refuses another
   * commit (CommitAdmissionPolicy, businessCaseAlreadySelected), so the ways to start one are
   * hidden rather than offered and then rejected.
   */
  hasSaleVehicle?: boolean;
  /**
   * Filling the same slot from stock. This card replaces the empty slot rather than sitting
   * under it, so while the slot is open it has to keep offering the slot's own action too.
   */
  stockAction?: {onClick: VoidFunction; isDisabled?: boolean};
};

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

function getRowAnnotation(configuration: {vehicleId: string | null; notSelected: boolean}) {
  // The vehicle it produced is rendered directly above, so the row names that link rather than
  // navigating away from it.
  if (isNotNil(configuration.vehicleId)) {
    return {
      label: t('entity.newVehicle.businessCaseConfigurations.saleVehicle', 'Sale vehicle'),
      colorScheme: 'blue' as const,
    };
  }
  if (configuration.notSelected) {
    return {
      label: t('entity.newVehicle.businessCaseConfigurations.notSelected', 'Not selected'),
      colorScheme: 'neutral' as const,
    };
  }
  return null;
}

export function BusinessCaseConfigurationsCard(props: BusinessCaseConfigurationsCardProps) {
  const navigate = useNavigate();
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [canRead, canWrite, canOrder] = usePermissions({
    permissionKeys: [
      'newVehicleConfigurationRead',
      'newVehicleConfigurationWrite',
      'newVehicleConfigurationOrder',
    ],
  });
  const isEnabled = isFeatureEnabled(featureFlags.NEW_VEHICLE_CATALOGUE);
  const query = useListBusinessCaseConfigurationsQuery(
    {businessCaseId: props.businessCaseId},
    {skip: !isEnabled || !canRead}
  );
  // Same cached query as the Selling section; withdrawn alternatives are not listed.
  const {
    data: offerAlternatives,
    isLoading: isLoadingAlternatives,
    isError: isAlternativesError,
    refetch: refetchAlternatives,
  } = useGetOfferAlternativesQuery({businessCaseId: props.businessCaseId}, {skip: !isEnabled});
  const alternativeConfigurationIds = (offerAlternatives?.alternatives ?? [])
    .map((alternative) => alternative.configurationId)
    .filter(isNotNil);
  const [getDetail] = useLazyGetConfigurationDetailQuery();
  const isMounted = useRef(true);
  const currentBusinessCaseId = useRef(props.businessCaseId);
  currentBusinessCaseId.current = props.businessCaseId;
  const detailRequestSequence = useRef(0);
  useOnMount(
    () => {
      isMounted.current = true;
    },
    () => {
      isMounted.current = false;
    }
  );
  const configurations = query.currentData;

  if (!isEnabled || !canRead) {
    return null;
  }
  const isSlotFilled = props.hasSaleVehicle === true;
  const hasConfigurations = isNotNil(configurations) && configurations.length > 0;
  // Offering a route the backend refuses is worse than offering none, so the new-vehicle routes
  // go and the stock route stays.
  const hasMaterializedConfiguration = holdsBusinessCaseSlot(configurations);
  // Cancellation releases the slot on its own where the backend can do so without a Sales
  // mutation; what is left here is a cancelled order still attached to this Business Case.
  const cancelled = configurations?.find(
    (configuration) =>
      isNotNil(configuration.vehicleId) && configuration.orderStatus === 'cancelled'
  );
  // A car that arrived before the order was cancelled is physically here and can still be sold;
  // only an unarrived one blocks the final invoice, so the two must not read the same.
  const hasArrived = cancelled?.hasArrived === true;
  // The slot is full and nothing was drafted on the way there: a stock sale has no business
  // showing a new-vehicle panel at all.
  if (isSlotFilled && !hasConfigurations) {
    return null;
  }

  const configurePath = composePath(vehiclesRoutes.newVehicleConfigurator, {
    params: {step: '1'},
    queryParams: {businessCaseId: props.businessCaseId},
  });
  const canConfirm =
    Boolean(canOrder) && ['CONCEPT', 'OFFER'].some((state) => state === props.businessCaseState);
  // Committing needs a free slot as well as the permission: the backend rejects a second commit
  // with businessCaseAlreadySelected, so offering it once a vehicle is attached is a dead end.
  const canCommit = canConfirm && !isSlotFilled && !hasMaterializedConfiguration;
  // A configuration offered as an alternative becomes the deal vehicle only by applying it.
  // Unknown until loaded, and still unknown after a failed lookup: no confirm button may appear
  // for a row that may be an alternative.
  const isOfferedAsAlternative = (configurationId: string) =>
    isLoadingAlternatives ||
    isAlternativesError ||
    alternativeConfigurationIds.includes(configurationId);

  if (!isSlotFilled && !hasConfigurations) {
    // Rendered while the list is still loading too: both routes are valid whatever it returns,
    // and waiting would blank the slot on every Business Case that has no configurations. A
    // failed list instead falls through to the card, so the error and its retry stay visible
    // rather than reading as "nothing configured yet".
    if (!query.isError) {
      return (
        <>
          <NewVehicleSlotChoice
            stockAction={props.stockAction}
            onConfigure={canWrite ? () => navigate(configurePath) : undefined}
            onUseExisting={canCommit ? () => setIsSelectOpen(true) : undefined}
          />
          <Show when={isSelectOpen}>
            <SelectConfigurationDialog
              businessCaseId={props.businessCaseId}
              isOpen={isSelectOpen}
              onClose={() => setIsSelectOpen(false)}
              onDone={query.refetch}
            />
          </Show>
        </>
      );
    }
  }

  const actions: Action[] = isSlotFilled
    ? []
    : [
        // Without this, stock becomes unreachable the moment a configuration exists: the parent
        // no longer renders the empty state that used to carry it.
        ...(isNotNil(props.stockAction)
          ? [
              {
                type: 'button' as const,
                variant: 'secondary' as const,
                title: i18n.t('entity.vehicle.actions.addVehicleToSell'),
                isDisabled: props.stockAction.isDisabled,
                onClick: props.stockAction.onClick,
              },
            ]
          : []),
        // Selecting commits the configuration, so it needs the ordering permission and a Business
        // Case that can still take one, not merely write access.
        ...(canCommit
          ? [
              {
                type: 'button' as const,
                variant: 'secondary' as const,
                title: t(
                  'entity.newVehicle.actions.useExistingConfiguration',
                  'Use existing configuration'
                ),
                onClick: () => setIsSelectOpen(true),
              },
            ]
          : []),
        // Configuring ends in the same commit, so it is gated by the same rule.
        ...(canWrite && !hasMaterializedConfiguration
          ? [
              {
                type: 'button' as const,
                variant: 'secondary' as const,
                title: t('entity.newVehicle.actions.configure', 'Configure new vehicle'),
                onClick: () => navigate(configurePath),
              },
            ]
          : []),
      ];
  const openOrder = (configurationId: string) => {
    const businessCaseId = props.businessCaseId;
    const sequence = detailRequestSequence.current + 1;
    detailRequestSequence.current = sequence;
    void getDetail({configurationId}, true)
      .unwrap()
      .then((configuration) => {
        if (
          !isMounted.current ||
          detailRequestSequence.current !== sequence ||
          currentBusinessCaseId.current !== businessCaseId
        ) {
          return;
        }
        const dialogId = getOrderConfigurationDialogId(configurationId);
        openDialog(
          <OrderConfigurationDialog
            configuration={configuration}
            dialogId={dialogId}
            onDone={query.refetch}
          />,
          {
            id: dialogId,
            title: t('entity.newVehicle.order.title', 'Order configuration'),
            size: 'large',
          }
        );
      })
      .catch(handleApiError);
  };

  return (
    <Box paddingTop={4}>
      <Card
        // Titled by what it produces: filling the slot above should not require knowing what a
        // "configuration" is.
        title={t('entity.newVehicle.labels.newVehicle', 'New vehicle')}
        actions={actions}
        data-testid="business-case-new-vehicle-configurations"
        colorStripe={isNotNil(cancelled) ? 'orange' : undefined}
      >
        <VStack spacing={4}>
          <Show when={isNotNil(cancelled)}>
            <Alert
              variant="warning"
              data-testid="business-case-new-vehicle-cancelled"
              title={t(
                'entity.newVehicle.businessCaseConfigurations.cancelledTitle',
                'Ordered vehicle cancelled'
              )}
              message={
                hasArrived
                  ? t(
                      'entity.newVehicle.businessCaseConfigurations.cancelledArrivedMessage',
                      'The order was cancelled after the vehicle arrived. The car is in stock, so this sale can still be completed.'
                    )
                  : t(
                      'entity.newVehicle.businessCaseConfigurations.cancelledMessage',
                      'This vehicle will not be delivered and the final invoice cannot be issued for it. Change the vehicle on this business case, or close it as unsuccessful.'
                    )
              }
            />
          </Show>
          {/* Only contract confirmation depends on the alternatives; the table stays usable. */}
          <Show when={isAlternativesError}>
            <Alert
              variant="warning"
              layout="detailed"
              data-testid="business-case-new-vehicle-alternatives-error"
              title={t(
                'entity.newVehicle.businessCaseConfigurations.alternativesErrorTitle',
                'Contract confirmation is unavailable'
              )}
              message={t(
                'entity.newVehicle.businessCaseConfigurations.alternativesErrorMessage',
                'The offer alternatives could not be loaded, so it is not known which configurations are offered as alternatives.'
              )}
              actions={[
                {
                  title: t('general.actions.tryAgain', 'Try again'),
                  variant: 'link',
                  onClick: () => void refetchAlternatives(),
                },
              ]}
            />
          </Show>
          <DataStatus
            isLoading={query.isLoading || query.isFetching}
            isError={query.isError}
            errorAction={{
              title: t('general.actions.tryAgain', 'Try again'),
              onClick: () => void query.refetch(),
              variant: 'secondary',
            }}
          >
            {/* No empty illustration: an empty list is drawn by the slot choice above instead. */}
            <Show when={isNotNil(configurations) && configurations.length > 0}>
              <ConfigurationsTable
                configurations={configurations ?? []}
                onReview={(configurationId) =>
                  navigate(
                    composePath(vehiclesRoutes.newVehicleConfigurator, {
                      params: {step: '5'},
                      queryParams: {configurationId},
                    })
                  )
                }
                renderAnnotation={(configuration) => {
                  const annotation = getRowAnnotation(configuration);
                  if (isNil(annotation)) {
                    return null;
                  }
                  return (
                    <Flag
                      size="small"
                      isSubtle
                      colorScheme={annotation.colorScheme}
                      label={annotation.label}
                    />
                  );
                }}
                renderAction={(configuration) =>
                  // eslint-disable-next-line eag/prefer-show -- Pre-existing violation
                  canCommit &&
                  configuration.vehicleId === null &&
                  !configuration.notSelected &&
                  !isOfferedAsAlternative(configuration.id) ? (
                    <Button
                      variant="secondary"
                      size="small"
                      title={t('entity.newVehicle.order.confirmContract', 'Confirm for contract')}
                      onClick={(event) => {
                        event.stopPropagation();
                        openOrder(configuration.id);
                      }}
                    />
                  ) : null
                }
              />
            </Show>
          </DataStatus>
        </VStack>
        <Show when={isSelectOpen}>
          <SelectConfigurationDialog
            businessCaseId={props.businessCaseId}
            isOpen={isSelectOpen}
            onClose={() => setIsSelectOpen(false)}
            onDone={query.refetch}
          />
        </Show>
      </Card>
    </Box>
  );
}
